import { ACHIEVEMENTS } from '../../data/achievements';
import { CHALLENGES } from '../../data/misc';
import { WIN_BALANCE, WIN_HENS } from '../../data/balance';
import type { AchCategory } from '../../data/types';
import { challengeProgress, challengeTiersReached, conditionProgress } from '../../core/achievements';
import { offlineCapMs, roosterCost } from '../../core/economy';
import { formatCount, formatDuration, formatMoney } from '../../core/format';
import { countHens, countRoosters } from '../../core/state';
import { game } from '../controller';
import { h, progressBar, setProgress, type Bindings } from '../dom';
import { card, section, tag, type Panel } from '../shop';

const CAT_LABEL: Record<AchCategory, string> = {
  eggs: 'Eggs collected', production: 'Production', hens: 'Hens', roosters: 'Roosters', diversity: 'Breed diversity',
  clicks: 'Clicks & golden eggs', special_eggs: 'Special eggs', secret: 'Secret', comedy: 'Silly birds',
  flock_build: 'Flock builds', broody: 'Broody', multi_flock: 'The long game', synergy: 'Rooster + hen pairs',
};

export const goalsPanel: Panel = {
  id: 'goals',
  label: 'Goals',
  key: () => `${game.state.achievements.length}|${game.state.won}|${JSON.stringify(game.state.challengeTiers)}`,
  build(root: HTMLElement, b: Bindings) {
    const s = game.state;
    const c = game.ctx();
    const stage = section(s.won ? '🏆 Stage 1 cleared' : '🎯 Stage 1: Hen House Champion');
    if (!s.won) {
      const rows: [string, () => number, number][] = [
        [`Bank ${formatMoney(WIN_BALANCE)}`, () => game.state.money, WIN_BALANCE],
        [`Own ${WIN_HENS} hens`, () => countHens(game.state), WIN_HENS],
        ['Hire a rooster', () => countRoosters(game.state), 1],
      ];
      for (const [label, cur, target] of rows) {
        const bar = progressBar(0);
        const val = h('span', { class: 'meta' });
        b.add(() => { const v = cur(); setProgress(bar, v / target); val.textContent = `${v >= target ? '✓' : ''} ${label === rows[0]![0] ? formatMoney(v) : formatCount(v)} / ${label === rows[0]![0] ? formatMoney(target) : target}`; });
        stage.append(card({ title: [label], meta: val, extra: bar }));
      }
    } else {
      const next = countRoosters(s) < 10 ? `Next rooster: ${formatMoney(roosterCost(s))}.` : 'All ten roosters hired!';
      stage.append(card({ icon: '🏆', title: ['The long game'], desc: `Grow to ten roosters. Your farm earns while you're away, up to ${formatDuration(offlineCapMs(s, c))} (+2h per rooster). ${next}` }));
    }
    root.append(stage);

    if (s.won) {
      const ch = section('⭐ Challenges');
      for (const def of CHALLENGES) {
        const bar = progressBar(0);
        const meta = h('span', { class: 'meta' });
        b.add(() => {
          const p = challengeProgress(game.state, def);
          setProgress(bar, p.current / Math.max(1, p.target));
          const reached = challengeTiersReached(game.state, def);
          meta.textContent = def.tiers
            ? `${reached ? `Title: ${def.tiers[reached - 1]!.label} · ` : ''}${formatMoney(p.current)} / ${formatMoney(p.target)}`
            : `${formatCount(p.current)} / ${formatCount(p.target)}`;
        });
        ch.append(card({ title: [def.name, s.challengeTiers[def.id] ? tag('✓') : null].filter(Boolean) as (Node | string)[], desc: def.description, meta, extra: bar }));
      }
      root.append(ch);
    }

    const earned = new Set(s.achievements);
    root.append(h('p', { class: 'hint', text: `Achievements: ${earned.size}/${ACHIEVEMENTS.length}. Each one counts toward Hen Wrangler upgrades.` }));
    const cats = [...new Set(ACHIEVEMENTS.map((a) => a.category))];
    for (const cat of cats) {
      const list = ACHIEVEMENTS.filter((a) => a.category === cat);
      const got = list.filter((a) => earned.has(a.id)).length;
      const det = h('details', { class: 'section', open: got < list.length && cat !== 'secret' ? undefined : undefined },
        h('summary', {}, `${CAT_LABEL[cat]} — ${got}/${list.length}`));
      for (const a of list) {
        const has = earned.has(a.id);
        const secret = a.hidden && !has;
        const p = conditionProgress(s, c, a.condition);
        det.append(card({
          cls: has ? '' : 'locked',
          icon: has ? '🏅' : secret ? '❔' : '⬜',
          title: [secret ? 'Secret achievement' : a.name],
          desc: secret ? 'Keep playing to discover this one.' : a.description,
          ...(has ? { flavor: a.flavor } : {}),
          ...(!has && !secret && p.target > 1 ? { extra: progressBar(p.current / p.target) } : {}),
        }));
      }
      root.append(det);
    }
  },
};
